import { memoizeFunction } from 'office-ui-fabric-react';
import { Resource } from '../../../../store/ResourcesListStore';
import { Dictionary } from '../../../../entities/common';
import { roundToHundreds } from '../../../utils/common';
import { tryGetTimephasedValueFromResource } from '../../../../services/TimephasedValueService';
import { calculateSummary, ISummaryInfo } from './Cell';
import { AssignmentsWork } from '../assignments';
import { CellSummaryContext, CellSummaryData, toHoursData } from './common';

export interface IAssignmentsSummaryInfo {
    fullFteCapacityHours: number;
    capacityHours: number;
    effort: number;
    completedWork: number;
    remainingWork: number;
    availability: number;
    overage: number;
    hasOverage: boolean;
    hasLocalOverage: boolean;
    estimatedCharge: number;
    estimatedCost: number;
    actualCharge: number;
    actualCost: number;
    otherProjectsEffort: Dictionary<number>;
}

export type AssignmentsByDay = Dictionary<AssignmentsWork & { resourceEffort?: number, otherProjectsEffort?: Dictionary<number> }>;

export function calculateAssignmentsSummary(startDate: Date, finishDate: Date, context: CellSummaryContext, byDay: AssignmentsByDay | undefined)
    : IAssignmentsSummaryInfo {
    const { resource, calendar, entityType, resourcePlanningLevel, defaultCostRateTableId } = context;
    const summary = calculateSummary(resource, startDate, finishDate, calendar, entityType, resourcePlanningLevel, defaultCostRateTableId);
    return calculateAssignmentsSummaryMemoized(resource, summary, byDay, defaultCostRateTableId);
}

export const calculateAssignmentsSummaryData = (startDate: Date, finishDate: Date, context: CellSummaryContext, byDay: AssignmentsByDay | undefined)
    : CellSummaryData => {
    const summary = calculateAssignmentsSummary(startDate, finishDate, context, byDay);
    return {
        hours: toHoursData(summary.effort, summary.completedWork, summary.remainingWork, summary.capacityHours, summary.availability, summary.fullFteCapacityHours),
        fullFteCapacityHours: summary.fullFteCapacityHours,
        hasOverage: summary.hasOverage,
        hasLocalOverage: summary.hasLocalOverage,
        hasProposedOverage: false,
        hasLocalProposedOverage: false
    };
}

const calculateAssignmentsSummaryMemoized = memoizeFunction(calculateAssignmentsSummaryImpl);

function calculateAssignmentsSummaryImpl(resource: Resource, summary: ISummaryInfo, byDay: AssignmentsByDay | undefined, defaultCostRateTableId?: string)
    : IAssignmentsSummaryInfo {
    const days = Object.keys(summary.capacityByDay).map(date => {
        const work = byDay?.[date];
        const dayEffort = work?.effort ?? 0;
        return {
            date,
            capacity: summary.capacityByDay[date],
            effort: dayEffort,
            completedWork: work?.completedWork ?? 0,
            remainingWork: work?.remainingWork ?? 0,
            resourceEffort: work?.resourceEffort ?? dayEffort,
            otherProjectsEffort: work?.otherProjectsEffort ?? {}
        };
    });

    const effort = roundToHundreds(days.reduce((sum, _) => sum + _.effort, 0));
    const completedWork = roundToHundreds(days.reduce((sum, _) => sum + _.completedWork, 0));
    const remainingWork = roundToHundreds(days.reduce((sum, _) => sum + _.remainingWork, 0));
    const resourceEffort = roundToHundreds(days.reduce((sum, _) => sum + _.resourceEffort, 0));
    const nonNeutralDays = days.filter(_ => _.capacity || _.resourceEffort);
    const overallocatedDays = nonNeutralDays.filter(_ => roundToHundreds(_.resourceEffort) > _.capacity);
    const costs = days
        .filter(_ => _.effort || _.completedWork)
        .reduce((cum, _) => {
            const date = new Date(_.date);
            const standardRate = tryGetTimephasedValueFromResource('StandardRate', resource, date, defaultCostRateTableId) ?? 0;
            const standardCost = tryGetTimephasedValueFromResource('StandardCost', resource, date, defaultCostRateTableId) ?? 0;
            return {
                estimatedCharge: cum.estimatedCharge + _.effort * standardRate,
                estimatedCost: cum.estimatedCost + _.effort * standardCost,
                actualCharge: cum.actualCharge + _.completedWork * standardRate,
                actualCost: cum.actualCost + _.completedWork * standardCost
            };
        }, { estimatedCharge: 0, estimatedCost: 0, actualCharge: 0, actualCost: 0 });
    const otherProjectsEffort = days.reduce<Dictionary<number>>((cum, _) => {
        Object.keys(_.otherProjectsEffort).forEach(id => cum[id] = roundToHundreds((cum[id] ?? 0) + _.otherProjectsEffort[id]));
        return cum;
    }, {});

    return {
        fullFteCapacityHours: summary.fullFteCapacityHours,
        capacityHours: summary.capacityHours,
        effort,
        completedWork,
        remainingWork,
        availability: Math.max(0, summary.capacityHours - resourceEffort),
        overage: resourceEffort > summary.capacityHours ? roundToHundreds(resourceEffort - summary.capacityHours) : 0,
        hasOverage: overallocatedDays.length > 0 && overallocatedDays.length === nonNeutralDays.length,
        hasLocalOverage: overallocatedDays.length > 0,
        estimatedCharge: roundToHundreds(costs.estimatedCharge),
        estimatedCost: roundToHundreds(costs.estimatedCost),
        actualCharge: roundToHundreds(costs.actualCharge),
        actualCost: roundToHundreds(costs.actualCost),
        otherProjectsEffort
    };
}
